import { useCallback, useEffect, useRef, useState } from 'react';
import { showToast } from '@ideoz/ui';
import { workbenchStore } from '@ideoz/workbench';
import { prototyperService, type Message } from '@ideoz/core';
import { useConversation } from '../context/ConversationContext';
import { shouldStartBuildStream } from '../../prototyper/utils/shouldStartBuildStream';
import { readBuiltTurnIds } from '../../prototyper/utils/builtTurnIds';
import { getWorkbenchTabId } from '../../prototyper/utils/versionTracker';
import {
  indexTurnVersions,
  turnVersionsPending,
  unfinishedBuildAction,
  type TurnVersion,
} from '../../prototyper/utils/turnVersions';
import {
  continueUnfinishedTurnAction,
  turnBuildDidNotFinish,
} from '../../prototyper/utils/unfinishedTurn';
import {
  isBenignMissingSnapshotError,
  isSupersededRestoreError,
} from '../../prototyper/utils/snapshotRestoreErrors';
import {
  shouldStartFixStreamAfterResume,
  type ResumeBuildOutcome,
} from '../../prototyper/utils/resumeBuildGating';
import {
  hasAttemptedResumeFix,
  markResumeFixAttempted,
} from '../../prototyper/utils/resumeFixAttempts';

/**
 * Owns the WebContainer build-stream orchestration for the conversation
 * workspace (extracted verbatim from `ConversationModule` in Phase 2, slice 4):
 * the refs + plan helpers + effects that decide *when* to auto-start a prototype
 * build once the assistant's plan has streamed in (deferring around snapshot
 * restore), plus the four prototype action handlers.
 *
 * Reads `facade`/`workbench`/`urlConversationId`/`isWeb` from `useConversation()`
 * — the same object identities the module used, so the moved effect dep arrays
 * are unchanged and behavior is preserved. The few non-context values come in as
 * params. `resetBuildStreamLatch` is the single external write to the "one
 * build-start per llmInteractionId" latch (called by the module's `handleSubmit`).
 */
interface UseConversationPrototypeStreamParams {
  isPrototypeOpen: boolean;
  setIsPrototypeOpen: (open: boolean) => void;
  isReplyStillActive: (
    submittedConversationId: string | null | undefined,
    newConversationId?: string
  ) => boolean;
  currentPrototypeId: number | null;
  hasPrototypeCredits: boolean;
}

export function useConversationPrototypeStream({
  isPrototypeOpen,
  setIsPrototypeOpen,
  isReplyStillActive,
  currentPrototypeId,
  hasPrototypeCredits,
}: UseConversationPrototypeStreamParams) {
  const { facade, workbench, urlConversationId, isWeb } = useConversation();
  const { currentMode } = facade.mode;
  const { isPrototyper } = facade.headers;
  const { currentConversationId, messages, isStreaming, currentProjectId, initiatedFromUncover } =
    facade.state;
  const { fetchPrototyperById } = facade.prototyper;
  const {
    isStreamingPrototype = false,
    onStreamingPrototypeStart,
    onStreamingWorkbenchPrototypePanelVisibilityChange,
    workbenchSnapshotBootstrap,
    getWorkbenchSnapshotIndexEntry,
    workbenchSnapshotIndexLoading,
    onPrototyperWorkbenchRestore,
    resumeInterruptedBuild,
    turnVersions,
    resumeFailedVersion,
    resumeDraftFromServerFiles,
    buildAgain,
  } = workbench;

  /** WebContainer: one build-stream start per prototyper assistant `llmInteractionId` (follow-up prompts). */
  const lastStreamingBuildLlmInteractionIdRef = useRef<string | undefined>(undefined);
  /** Opened panel while snapshot bootstrap was `pending`; call build-stream once it settles. */
  const pendingDeferredWorkbenchStreamRef = useRef(false);
  /** Plan id held while waiting for snapshot bootstrap to finish. */
  const pendingDeferredPlanIdRef = useRef<string | undefined>(undefined);
  const lastModuleResetConversationIdRef = useRef<string | null | undefined>(undefined);
  const onStreamingWorkbenchPrototypePanelVisibilityChangeRef = useRef(
    onStreamingWorkbenchPrototypePanelVisibilityChange
  );
  useEffect(() => {
    onStreamingWorkbenchPrototypePanelVisibilityChangeRef.current =
      onStreamingWorkbenchPrototypePanelVisibilityChange;
  }, [onStreamingWorkbenchPrototypePanelVisibilityChange]);

  /** The only external write to the build-start latch (module's `handleSubmit` calls this). */
  const resetBuildStreamLatch = useCallback(() => {
    lastStreamingBuildLlmInteractionIdRef.current = undefined;
  }, []);

  /** One local-resume attempt per turn per conversation session (survives the build-stream latch reset). */
  const resumeStartedIdsRef = useRef<Set<string>>(new Set());

  /**
   * A turn was snapshotted at build-start but never verified — the build was interrupted
   * (refresh, lost tab). Finish it locally; only a genuine local build failure falls back to
   * a real LLM fix-stream, and only when the user has credits to spend on it.
   */
  const runResumeInterruptedBuild = useCallback(
    (llmInteractionId: string, options?: { userRequested?: boolean }) => {
      if (options?.userRequested) resumeStartedIdsRef.current.delete(llmInteractionId);
      if (resumeStartedIdsRef.current.has(llmInteractionId)) return;
      resumeStartedIdsRef.current.add(llmInteractionId);
      const conversationKey = currentConversationId ?? undefined;
      // At most one automatic fix stream per turn, remembered across reloads and tabs, so
      // reopening a conversation whose last build failed never starts another paid stream.
      const startFixStreamOnce = (outcome: ResumeBuildOutcome): void => {
        if (
          !shouldStartFixStreamAfterResume({
            outcome,
            hasPrototypeCredits,
            alreadyAttempted: hasAttemptedResumeFix(conversationKey, llmInteractionId),
          })
        ) {
          return;
        }
        markResumeFixAttempted(conversationKey, llmInteractionId);
        onStreamingPrototypeStart?.({ llmInteractionId });
      };
      if (!resumeInterruptedBuild) {
        // Resume wiring isn't available yet (panel not gated open). That is not a build
        // failure, so it must not spend credits; release the id so a later attempt can run.
        resumeStartedIdsRef.current.delete(llmInteractionId);
        return;
      }
      return resumeInterruptedBuild(llmInteractionId, options).then((outcome) => {
        if (outcome === 'skipped') {
          resumeStartedIdsRef.current.delete(llmInteractionId);
          return;
        }
        startFixStreamOnce(outcome);
      });
    },
    [resumeInterruptedBuild, onStreamingPrototypeStart, hasPrototypeCredits, currentConversationId]
  );

  /**
   * A reopened turn whose version failed with an automatic fix attempt left: restore its files
   * and start the fix (P6.4c). Once per turn per conversation session, like a resume.
   */
  const runResumeFailedVersion = useCallback(
    (llmInteractionId: string, options?: { userRequested?: boolean }) => {
      if (options?.userRequested) resumeStartedIdsRef.current.delete(llmInteractionId);
      if (!resumeFailedVersion || resumeStartedIdsRef.current.has(llmInteractionId)) return;
      resumeStartedIdsRef.current.add(llmInteractionId);
      return resumeFailedVersion(llmInteractionId).then((outcome) => {
        if (outcome !== 'skipped') return;
        resumeStartedIdsRef.current.delete(llmInteractionId);
      });
    },
    [resumeFailedVersion]
  );

  /**
   * The latest plan turn has a build session that never finished and no snapshot to resume
   * (the tab closed during planning or file writing). Starting a build here would spend credits
   * without the founder asking, and skipping silently left no prototype and no way forward, so
   * show the prototype error state — its Retry (`handleRetryPrototyper`) starts the build.
   */
  const turnVersionsRef = useRef(turnVersions);
  turnVersionsRef.current = turnVersions;
  const reportUnfinishedBuild = useCallback(
    (llmInteractionId: string | undefined, options?: { userRequested?: boolean }) => {
      // A turn with a draft version already says so on its pill, "Didn't finish" with Continue,
      // which is the way forward. The error state as well replaced the version on screen with an
      // error and put a generic "Something went wrong" banner under the chat (after a refresh
      // during a build).
      const versions = turnVersionsRef.current;
      const version =
        llmInteractionId && versions?.status === 'ready' ? versions.get(llmInteractionId) : undefined;
      // A Continue the founder pressed that could not finish still reports it.
      if (version?.status === 'draft' && !options?.userRequested) return;
      facade.prototyper.reportError("The last prototype build didn't finish. Retry to build it.");
    },
    [facade.prototyper]
  );

  /**
   * A reopened unfinished draft with no build-start snapshot: build it from the files the builder
   * saved for its turn (P6.4d-3). When the builder has none for it, show the retry state.
   */
  const runResumeDraftFromFiles = useCallback(
    (
      llmInteractionId: string,
      options?: {
        userRequested?: boolean;
        /** Continue pressed and the builder has no files: what to do instead of the error state. */
        onNothingSaved?: () => void;
      }
    ) => {
      if (!resumeDraftFromServerFiles) {
        reportUnfinishedBuild(llmInteractionId, options);
        return;
      }
      if (options?.userRequested) resumeStartedIdsRef.current.delete(llmInteractionId);
      if (resumeStartedIdsRef.current.has(llmInteractionId)) return;
      resumeStartedIdsRef.current.add(llmInteractionId);
      return resumeDraftFromServerFiles(llmInteractionId).then((outcome) => {
        if (outcome === 'files_unavailable' && options?.onNothingSaved) {
          resumeStartedIdsRef.current.delete(llmInteractionId);
          options.onNothingSaved();
        } else if (outcome === 'files_unavailable' || outcome === 'environment_failure') {
          reportUnfinishedBuild(llmInteractionId, options);
        } else if (outcome === 'skipped') {
          resumeStartedIdsRef.current.delete(llmInteractionId);
        }
      });
    },
    [resumeDraftFromServerFiles, reportUnfinishedBuild]
  );

  // Streaming workbench: mirror prototype-panel visibility to the parent so it
  // can defer the WebContainer boot until the panel is actually open.
  useEffect(() => {
    if (!isStreamingPrototype || !onStreamingWorkbenchPrototypePanelVisibilityChange) return;
    onStreamingWorkbenchPrototypePanelVisibilityChange(isPrototypeOpen);
  }, [isStreamingPrototype, isPrototypeOpen, onStreamingWorkbenchPrototypePanelVisibilityChange]);

  /**
   * When using WebContainer build-stream, only start after conversation streaming finishes,
   * the assistant plan exists in Redux, and Nest has attached `llmInteractionId` to that turn.
   */
  const isWorkbenchStreamingPlanReady = useCallback(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    const text = lastAssistant?.content?.trim() ?? '';
    if (!text) return false;
    // The scope's one-shot `…/new` auto-prompt greeting streams in as the opening
    // message with no preceding user bubble (see useConversationPageFacade's
    // greeting effect) — the backend can still tag it `mode: 'prototyper'` when
    // continuing a scope that's mid-build, but it's a conversational "welcome
    // back" turn, never an actionable build plan. Without a real user turn behind
    // it, never auto-start a build-stream for it.
    if (!messages.some((m) => m.role === 'user')) return false;
    // `prototypeReady` is the only valid signal — it's set exclusively from the
    // turn's own `prototype_ready` event (live) or its persisted twin (history).
    // There is deliberately no length/header-based fallback: `isPrototyper` is a
    // conversation-level flag that stays true after any past prototype_ready turn,
    // so a long *clarifying* reply on a later, non-building turn must not be
    // mistaken for a build plan.
    return lastAssistant?.prototypeReady === true;
  }, [messages]);

  /**
   * Latest assistant message that qualifies as the WebContainer prototyper plan
   * (same rules as {@link isWorkbenchStreamingPlanReady}).
   */
  const getLatestPrototyperWorkbenchPlanMessage = useCallback(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant) return undefined;
    const text = lastAssistant.content?.trim() ?? '';
    if (!text) return undefined;
    if (!messages.some((m) => m.role === 'user')) return undefined;
    return lastAssistant.prototypeReady === true ? lastAssistant : undefined;
  }, [messages]);

  /**
   * Handle prototyper header - open prototype panel when x-prototyper header is received
   */
  useEffect(() => {
    const wbBootstrap = workbenchSnapshotBootstrap ?? 'none';
    const cannotStartBase = !isPrototyper || !currentConversationId;

    if (cannotStartBase) {
      return;
    }

    const switchToPrototyperModeIfNeeded = (): void => {
      if (currentMode !== 'prototyper' && !initiatedFromUncover) {
        // showToast({
        //   message: 'Switching to Prototyper Mode...',
        //   severity: 'success',
        //   position: 'top-center',
        // });
        facade.mode.handleModeChange('prototyper');
      }
    };

    const openPanelAndStreamingCreate = (planLlmInteractionId: string): void => {
      switchToPrototyperModeIfNeeded();
      setIsPrototypeOpen(true);

      const decision = shouldStartBuildStream({
        planLlmInteractionId,
        builtTurnIds: readBuiltTurnIds(currentConversationId ?? undefined),
        turnVersions,
        ownerTabId: getWorkbenchTabId(),
        bootstrap: wbBootstrap,
        planMessage: getLatestPrototyperWorkbenchPlanMessage(),
        getSnapshotIndexEntry: getWorkbenchSnapshotIndexEntry,
        snapshotIndexLoading: workbenchSnapshotIndexLoading,
      });

      if (decision === 'skip') {
        pendingDeferredWorkbenchStreamRef.current = false;
        pendingDeferredPlanIdRef.current = undefined;
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        return;
      }
      if (decision === 'unfinished') {
        pendingDeferredWorkbenchStreamRef.current = false;
        pendingDeferredPlanIdRef.current = undefined;
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        reportUnfinishedBuild(planLlmInteractionId);
        return;
      }
      if (decision === 'wait') {
        pendingDeferredWorkbenchStreamRef.current = true;
        pendingDeferredPlanIdRef.current = planLlmInteractionId;
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        return;
      }
      if (decision === 'resume-build') {
        pendingDeferredWorkbenchStreamRef.current = false;
        pendingDeferredPlanIdRef.current = undefined;
        // Latch before the async resume settles — this is the primary re-entry guard against
        // a second effect run (StrictMode, re-render) starting a second resume for this turn.
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        runResumeInterruptedBuild(planLlmInteractionId);
        return;
      }
      if (decision === 'resume-fix') {
        pendingDeferredWorkbenchStreamRef.current = false;
        pendingDeferredPlanIdRef.current = undefined;
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        runResumeFailedVersion(planLlmInteractionId);
        return;
      }
      if (decision === 'resume-from-files') {
        pendingDeferredWorkbenchStreamRef.current = false;
        pendingDeferredPlanIdRef.current = undefined;
        lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
        runResumeDraftFromFiles(planLlmInteractionId);
        return;
      }
      pendingDeferredWorkbenchStreamRef.current = false;
      pendingDeferredPlanIdRef.current = undefined;
      onStreamingPrototypeStart?.({ llmInteractionId: planLlmInteractionId });
      lastStreamingBuildLlmInteractionIdRef.current = planLlmInteractionId;
    };

    // WebWorkbench streaming: defer until SSE finished, plan ready, and llmInteractionId persisted.
    if (isStreamingPrototype) {
      if (isStreaming) {
        return;
      }
      if (!isWorkbenchStreamingPlanReady()) {
        return;
      }

      const planMsg = getLatestPrototyperWorkbenchPlanMessage();
      const planLlmId = planMsg?.llmInteractionId;
      if (!planLlmId) {
        return;
      }
      if (planLlmId === lastStreamingBuildLlmInteractionIdRef.current) {
        return;
      }
      openPanelAndStreamingCreate(planLlmId);
      return;
    }

    return undefined;
  }, [
    isPrototyper,
    currentConversationId,
    facade.mode,
    currentMode,
    initiatedFromUncover,
    isStreamingPrototype,
    onStreamingPrototypeStart,
    workbenchSnapshotBootstrap,
    isStreaming,
    isWorkbenchStreamingPlanReady,
    getLatestPrototyperWorkbenchPlanMessage,
    messages,
    getWorkbenchSnapshotIndexEntry,
    workbenchSnapshotIndexLoading,
    runResumeInterruptedBuild,
    runResumeFailedVersion,
    runResumeDraftFromFiles,
    reportUnfinishedBuild,
    turnVersions,
  ]);

  /** If the panel opened while S3 restore was still `pending`, start build-stream once bootstrap settles. */
  useEffect(() => {
    const wb = workbenchSnapshotBootstrap ?? 'none';
    if (!isStreamingPrototype || wb === 'pending') return;
    if (!isPrototypeOpen) return;
    if (isStreaming) return;
    if (!pendingDeferredWorkbenchStreamRef.current) return;

    const planMsg = getLatestPrototyperWorkbenchPlanMessage();
    const planId = pendingDeferredPlanIdRef.current ?? planMsg?.llmInteractionId;
    if (!planId) return;

    const decision = shouldStartBuildStream({
      planLlmInteractionId: planId,
      builtTurnIds: readBuiltTurnIds(currentConversationId ?? undefined),
      turnVersions,
      ownerTabId: getWorkbenchTabId(),
      bootstrap: wb,
      planMessage: planMsg,
      getSnapshotIndexEntry: getWorkbenchSnapshotIndexEntry,
      snapshotIndexLoading: workbenchSnapshotIndexLoading,
    });

    if (decision === 'wait') return;

    pendingDeferredWorkbenchStreamRef.current = false;
    pendingDeferredPlanIdRef.current = undefined;

    if (decision === 'skip') {
      lastStreamingBuildLlmInteractionIdRef.current = planId;
      return;
    }

    if (decision === 'unfinished') {
      lastStreamingBuildLlmInteractionIdRef.current = planId;
      reportUnfinishedBuild(planId);
      return;
    }

    if (decision === 'resume-build') {
      lastStreamingBuildLlmInteractionIdRef.current = planId;
      runResumeInterruptedBuild(planId);
      return;
    }

    if (decision === 'resume-fix') {
      lastStreamingBuildLlmInteractionIdRef.current = planId;
      runResumeFailedVersion(planId);
      return;
    }

    if (decision === 'resume-from-files') {
      lastStreamingBuildLlmInteractionIdRef.current = planId;
      runResumeDraftFromFiles(planId);
      return;
    }

    onStreamingPrototypeStart?.({ llmInteractionId: planId });
    lastStreamingBuildLlmInteractionIdRef.current = planId;
  }, [
    isStreamingPrototype,
    workbenchSnapshotBootstrap,
    isPrototypeOpen,
    isStreaming,
    onStreamingPrototypeStart,
    getLatestPrototyperWorkbenchPlanMessage,
    currentConversationId,
    getWorkbenchSnapshotIndexEntry,
    workbenchSnapshotIndexLoading,
    runResumeInterruptedBuild,
    runResumeFailedVersion,
    runResumeDraftFromFiles,
    reportUnfinishedBuild,
    turnVersions,
  ]);

  /**
   * Reset prototype streaming refs when conversation changes (not on mount).
   */
  useEffect(() => {
    const id = currentConversationId ?? null;
    if (lastModuleResetConversationIdRef.current === undefined) {
      lastModuleResetConversationIdRef.current = id;
      return;
    }
    if (lastModuleResetConversationIdRef.current === id) return;
    lastModuleResetConversationIdRef.current = id;
    lastStreamingBuildLlmInteractionIdRef.current = undefined;
    pendingDeferredWorkbenchStreamRef.current = false;
    pendingDeferredPlanIdRef.current = undefined;
    resumeStartedIdsRef.current = new Set();
    setIsPrototypeOpen(false);
    onStreamingWorkbenchPrototypePanelVisibilityChangeRef.current?.(false);
  }, [currentConversationId]);

  /**
   * Start prototype generation from Uncover (the "+" Generate Prototype button, or
   * slash `/prototype` after the chip is submitted). Credit-checks, switches UI
   * mode to prototyper, and sends `forced_artifact: 'prototype'` so the backend
   * does not infer the deliverable from the user message.
   */
  const handleGeneratePrototypeFromUncover = useCallback(
    (message?: string, attachmentIds: string[] = [], productType?: string) => {
      if (!currentConversationId) return;

      // Check for prototype credits before proceeding
      if (!hasPrototypeCredits) {
        // Navigate to billing if no prototype credits
        facade.navigation.toBilling();
        return;
      }

      // Mark that this prototype was initiated from uncover mode
      // This prevents the mode switch in the prototyper header effect
      facade.conversation.setInitiatedFromUncover(true);
      // Temporarily switch to prototyper mode for the API request
      facade.conversation.setMode('prototyper');

      // Reset header states before submitting
      facade.headers.setIsClarification(false);
      facade.headers.setIsReflection(false);
      facade.headers.setIsRepeated(false);
      facade.headers.setIsPrototyper(false);
      facade.headers.setIsReadyToCreate(false);
      lastStreamingBuildLlmInteractionIdRef.current = undefined;

      // `message` is the founder's optional typed context only (the composer chip
      // no longer bakes the "Generate a … prototype" instruction into it). This
      // function owns that instruction so the product-type phrase is always in the
      // prose and never overwritten. When a product type was chosen from the
      // Generate Prototype dropdown, name it in the request so the prototype targets
      // that type (web app / website / mobile).
      // The choice has to travel as a value, not only as words in the message: the AI API pins
      // the spec's mode from `product_type`, and `projects.projectType` lists every type the
      // startup offers rather than the one this build is. Naming it in the prose as well keeps
      // the transcript readable. Setting it here makes it stick for the turns that follow.
      if (productType) facade.actions.setProductType(productType);

      const productTypePhrase: Record<string, string> = {
        web_app: 'web app',
        website: 'website',
        mobile: 'mobile app',
      };
      const phrase = productType ? productTypePhrase[productType] : undefined;
      const basePrompt = phrase ? `Generate a ${phrase} prototype` : 'Generate a prototype';
      const context = typeof message === 'string' ? message.trim() : '';
      const prompt = context ? `${basePrompt}\n\n${context}` : basePrompt;
      facade.actions.submitMessage(
        prompt,
        attachmentIds,
        (newConversationId, headers) => {
          // Process headers when streaming completes — but only if the user hasn't since
          // navigated to a different conversation (see isReplyStillActive).
          // The normal header flow will create the prototype when x-prototyper header is received
          if (headers && isReplyStillActive(currentConversationId, newConversationId)) {
            facade.headers.processHeaders(
              headers,
              currentConversationId,
              newConversationId,
              undefined,
              urlConversationId,
              facade.state.conversationId || undefined
            );
          }
        },
        'prototyper',
        undefined,
        'prototype'
      );
    },
    [currentConversationId, facade, hasPrototypeCredits, isReplyStillActive, urlConversationId]
  );

  /**
   * Handle "Back to Uncover" button click
   * Closes the prototype panel and returns to full-width Uncover chat
   */
  const handleBackToUncover = useCallback(() => {
    facade.conversation.setMode('clarify');
    setIsPrototypeOpen(false);
  }, [facade.conversation, setIsPrototypeOpen]);

  /**
   * Handle retry prototyper creation
   */
  const handleRetryPrototyper = useCallback(() => {
    if (!currentConversationId) {
      return;
    }
    facade.prototyper.clearError();
    if (isStreamingPrototype) {
      if (isStreaming) {
        showToast({
          message: 'Wait for the plan to finish streaming…',
          severity: 'info',
          position: 'top-center',
        });
        return;
      }
      if (!isWorkbenchStreamingPlanReady()) {
        showToast({
          message: 'Prototype plan is not ready yet.',
          severity: 'warning',
          position: 'top-center',
        });
        return;
      }
      const planMsg = getLatestPrototyperWorkbenchPlanMessage();
      const id = planMsg?.llmInteractionId;
      if (!id) {
        showToast({
          message: 'Waiting for the prototype plan to be saved…',
          severity: 'info',
          position: 'top-center',
        });
        return;
      }
      onStreamingPrototypeStart?.({ llmInteractionId: id });
      return;
    }
    showToast({
      message: 'Prototype generation is only available in the web workbench.',
      severity: 'warning',
      position: 'top-center',
    });
  }, [
    currentConversationId,
    facade.prototyper,
    isStreaming,
    isStreamingPrototype,
    isWorkbenchStreamingPlanReady,
    getLatestPrototyperWorkbenchPlanMessage,
    onStreamingPrototypeStart,
  ]);

  /**
   * Continue on an unfinished turn's pill (test finding 2). The automatic resume above only ever
   * looks at the latest plan turn, runs once, and can skip without a word; this runs for any turn
   * the founder points at, decided from the server's version rather than this tab's history.
   * Returns false when there is nothing to continue and the pill should restore as usual.
   */
  const continueUnfinishedTurn = useCallback(
    async (llmInteractionId: string, planMessage: Message | undefined): Promise<boolean> => {
      // The workbench loads versions only once the panel is open, and Continue is usually pressed
      // before that. Without them the decision falls to this tab's own rules, which cannot see a
      // draft and would skip the free resume for a paid rebuild; so read them now.
      let versions = turnVersions;
      if (versions?.status !== 'ready' && currentProjectId && currentConversationId) {
        try {
          const byTurn = indexTurnVersions(
            (await prototyperService.listConversationTurnVersions(
              currentProjectId,
              currentConversationId
            )) as TurnVersion[]
          );
          versions = { status: 'ready', get: (id) => byTurn.get(id) };
        } catch {
          // Unanswered: decide from what this tab has, as before.
        }
      }
      const decision = shouldStartBuildStream({
        planLlmInteractionId: llmInteractionId,
        // This tab's list of built turns is what went wrong here; ask the server only.
        builtTurnIds: new Set<string>(),
        turnVersions: versions,
        ownerTabId: getWorkbenchTabId(),
        bootstrap: workbenchSnapshotBootstrap ?? 'none',
        planMessage,
        getSnapshotIndexEntry: getWorkbenchSnapshotIndexEntry,
        snapshotIndexLoading: workbenchSnapshotIndexLoading,
      });
      const isLatestPlanTurn =
        getLatestPrototyperWorkbenchPlanMessage()?.llmInteractionId === llmInteractionId;
      const action = continueUnfinishedTurnAction(decision, isLatestPlanTurn);
      // No toasts along this path: the pill itself shows the state (busy while continuing, then
      // the built version, or "Didn't finish" again), and product review found the toasts
      // misleading next to it.
      const trackContinue = (work: Promise<unknown> | undefined) => {
        if (!work) return;
        setRebuildingTurnId(null);
        setContinuingTurnId(llmInteractionId);
        void work.finally(() => {
          if (!rebuildStartingRef.current) {
            setContinuingTurnId((current) => (current === llmInteractionId ? null : current));
          }
          // A resume builds in place; a rebuild clears the flag itself once it streams.
          if (!workbenchStore.isStreaming.get()) {
            window.setTimeout(() => {
              if (!workbenchStore.isStreaming.get()) workbenchStore.continuingBuild.set(false);
            }, 3_000);
          }
        });
      };
      // Nothing was saved to finish from, so continuing means building it again: the same path
      // as the Retry banner, which the founder no longer has to go looking for.
      // Covers the panel with "Finishing this build…" from the press until the build is running,
      // so the starter page underneath is never shown as "No saved version".
      workbenchStore.continuingBuild.set(true);
      // The pill stays busy until the new build streams (the listener below), not only until
      // this call returns: in between it went back to "Didn't finish · Continue" for seconds.
      const rebuildLatestTurn = () => {
        rebuildStartingRef.current = true;
        setRebuildingTurnId(llmInteractionId);
        setContinuingTurnId(llmInteractionId);
        window.setTimeout(() => {
          // The build never started (Retry refused it): hand the pill back.
          if (rebuildStartingRef.current && !workbenchStore.isStreaming.get()) {
            rebuildStartingRef.current = false;
            setContinuingTurnId(null);
            workbenchStore.continuingBuild.set(false);
          }
        }, 30_000);
        // From the last finished version when the workbench can ("Build again");
        // the plain retry builds on top of whatever the stopped build left.
        if (buildAgain) {
          void buildAgain(llmInteractionId).then((started) => {
            if (started || !rebuildStartingRef.current) return;
            rebuildStartingRef.current = false;
            setContinuingTurnId(null);
            workbenchStore.continuingBuild.set(false);
          });
          return;
        }
        handleRetryPrototyper();
      };
      // An older turn with nothing saved cannot be rebuilt in place; the pill keeps saying
      // "Didn't finish", and asking for the change again is the way forward.
      const askAgain = () => {
        setContinuingTurnId(null);
        workbenchStore.continuingBuild.set(false);
      };

      switch (action) {
        case 'restore':
          setContinuingTurnId(null);
          workbenchStore.continuingBuild.set(false);
          return false;
        case 'wait':
          // Pressing Continue opens the panel, which starts loading the prototype's history;
          // the effect below presses it again once that settles, so one press is enough.
          pendingContinueRef.current = { llmInteractionId, planMessage };
          // The pill is busy from the press, not only once the history has loaded.
          setContinuingTurnId(llmInteractionId);
          return true;
        case 'resume-build':
          lastStreamingBuildLlmInteractionIdRef.current = llmInteractionId;
          trackContinue(runResumeInterruptedBuild(llmInteractionId, { userRequested: true }));
          return true;
        case 'resume-fix':
          lastStreamingBuildLlmInteractionIdRef.current = llmInteractionId;
          trackContinue(runResumeFailedVersion(llmInteractionId, { userRequested: true }));
          return true;
        case 'resume-from-files':
          lastStreamingBuildLlmInteractionIdRef.current = llmInteractionId;
          trackContinue(
            runResumeDraftFromFiles(llmInteractionId, {
            userRequested: true,
            // A build stopped while writing files can leave the builder with none (it answers
            // 409). Continue then means building it again, not an error that asks for a second
            // click on "Try again" (seen on local, refresh during "Writing files").
            onNothingSaved: isLatestPlanTurn ? rebuildLatestTurn : askAgain,
            })
          );
          return true;
        case 'retry':
          rebuildLatestTurn();
          return true;
        case 'ask-again':
          askAgain();
          return true;
      }
    },
    [
      turnVersions,
      currentProjectId,
      currentConversationId,
      workbenchSnapshotBootstrap,
      getWorkbenchSnapshotIndexEntry,
      workbenchSnapshotIndexLoading,
      getLatestPrototyperWorkbenchPlanMessage,
      runResumeInterruptedBuild,
      runResumeFailedVersion,
      runResumeDraftFromFiles,
      handleRetryPrototyper,
      buildAgain,
    ]
  );

  /**
   * The turn a pressed Continue is finishing right now. Its pill shows a busy state meanwhile;
   * without it the pill kept "Didn't finish" and a live Continue button through a resume that
   * takes 20-40 s (seen on local). A rebuild needs none: the live build shows its own.
   */
  const [continuingTurnId, setContinuingTurnId] = useState<string | null>(null);
  /** Continue fell through to building the turn again, and that build has not streamed yet. */
  const rebuildStartingRef = useRef(false);
  /** The turn being built again rather than finished: its busy card says "Starting…". */
  const [rebuildingTurnId, setRebuildingTurnId] = useState<string | null>(null);
  const continuingTurnLabel = continuingTurnId
    ? continuingTurnId === rebuildingTurnId
      ? 'Starting…'
      : 'Finishing…'
    : undefined;

  /** A Continue that had to wait for the prototype's history to load; retried by the effect below. */
  const pendingContinueRef = useRef<{
    llmInteractionId: string;
    planMessage: Message | undefined;
  } | null>(null);
  useEffect(() => {
    const pending = pendingContinueRef.current;
    if (!pending) return;
    if (workbenchSnapshotIndexLoading || workbenchSnapshotBootstrap === 'pending') return;
    if (turnVersionsPending(turnVersions)) return;
    pendingContinueRef.current = null;
    void continueUnfinishedTurn(pending.llmInteractionId, pending.planMessage);
  }, [
    workbenchSnapshotIndexLoading,
    workbenchSnapshotBootstrap,
    turnVersions,
    continueUnfinishedTurn,
  ]);
  // A conversation switch drops a Continue that was still waiting.
  useEffect(() => {
    pendingContinueRef.current = null;
    setContinuingTurnId(null);
    workbenchStore.continuingBuild.set(false);
  }, [currentConversationId]);
  // The build Continue started is running: its own progress takes over from "Finishing this build…".
  useEffect(
    () =>
      workbenchStore.isStreaming.listen((streaming) => {
        if (!streaming) return;
        workbenchStore.continuingBuild.set(false);
        rebuildStartingRef.current = false;
        setContinuingTurnId(null);
        setRebuildingTurnId(null);
      }),
    []
  );

  /**
   * Handle prototyper banner click
   * If prototyper_demo_link_id is provided, fetch the prototyper data
   * Otherwise, just open the panel (already created via header)
   */
  const handlePrototyperClick = useCallback(
    async (
      prototyperDemoLinkId?: string | number,
      meta?: {
        llmInteractionId?: string | null;
        continueBuild?: boolean;
        didNotFinish?: boolean;
        /** Any turn of this conversation has a finished version the panel could show. */
        conversationHasFinishedVersion?: boolean;
      }
    ) => {
      const lid = meta?.llmInteractionId ?? undefined;
      // An unfinished pill opened with nothing finished anywhere in the conversation would open
      // the panel on an empty workspace, which said "No saved version of this prototype yet"
      // beside a pill that says "Didn't finish". Continue is the way forward there, not the panel.
      if (
        meta?.didNotFinish === true &&
        !meta.continueBuild &&
        meta.conversationHasFinishedVersion === false
      ) {
        return;
      }
      // The panel is auto-closed by the mode-change effect at line ~927
      // whenever `currentMode === 'clarify'`. Clicking "View prototype"
      // needs the mode to flip first so that effect doesn't cancel out
      // the panel we're about to open. Same switch the "Generate
      // Prototype" button uses.
      if (currentMode !== 'prototyper') {
        facade.mode.handleModeChange('prototyper');
      }
      setIsPrototypeOpen(true);

      if (
        lid &&
        onPrototyperWorkbenchRestore &&
        isStreamingPrototype &&
        currentProjectId &&
        currentConversationId
      ) {
        const sourceMessage = [...messages]
          .reverse()
          .find((m) => m.role === 'assistant' && m.llmInteractionId === lid);
        // What the pill showed when it was clicked, so the click does what it offered; the pill
        // reads versions even while the panel (and with it `turnVersions` here) is closed.
        const unfinished =
          meta?.didNotFinish ??
          (sourceMessage != null &&
            turnBuildDidNotFinish({
              message: sourceMessage,
              version: turnVersions?.status === 'ready' ? turnVersions.get(lid) : undefined,
              indexEntry: getWorkbenchSnapshotIndexEntry?.(lid),
              ownerTabId: getWorkbenchTabId(),
            }));
        if (unfinished) {
          // Only the Continue button starts work: resuming is free, but its fallback builds the
          // turn again and spends credits, which opening a pill must never do on its own.
          if (meta?.continueBuild) {
            // A build that failed is not continued: its request is built again from the last
            // finished version (Task 722). The button already says so.
            const version = turnVersions?.status === 'ready' ? turnVersions.get(lid) : undefined;
            if (buildAgain && unfinishedBuildAction(version?.status) === 'build-again') {
              lastStreamingBuildLlmInteractionIdRef.current = lid;
              // The card is busy from the press until the new build streams. Build again turns
              // the version back into a draft first, and in between the card offered Continue
              // for seconds (test, 15 Oct 2026). The streaming listener below hands it back.
              rebuildStartingRef.current = true;
              setRebuildingTurnId(lid);
              setContinuingTurnId(lid);
              const handBack = () => {
                if (!rebuildStartingRef.current || workbenchStore.isStreaming.get()) return;
                rebuildStartingRef.current = false;
                setContinuingTurnId((current) => (current === lid ? null : current));
              };
              // Started but never streamed: hand the card back, as `rebuildLatestTurn` does.
              window.setTimeout(handBack, 30_000);
              if (!(await buildAgain(lid))) handBack();
              return;
            }
            if (await continueUnfinishedTurn(lid, sourceMessage)) return;
          } else {
            // Opening the panel is all a plain click does; the pill already says what happened.
            return;
          }
        }
        try {
          await onPrototyperWorkbenchRestore(lid, {
            buildStatus: sourceMessage?.buildStatus,
            hasBuildTimeline: Boolean(sourceMessage?.buildTimeline),
          });
        } catch (error) {
          console.error('Failed to restore prototype snapshot:', error);
          if (isSupersededRestoreError(error)) {
            return;
          }
          // A message can carry `hasPrototype: true` (rendering this pill) for a turn
          // whose build never actually ran or hasn't finished yet — the pill signals
          // "this is a prototyper turn," not "a build definitely exists." Surfacing
          // that as a scary red error is misleading; say what's actually true instead.
          if (isBenignMissingSnapshotError(error)) {
            // Nothing to show for this turn. No toast: "This turn hasn't built a prototype yet"
            // misled founders whose build had only been interrupted (test finding 2); the pill
            // shows "Didn't finish" with Continue for those instead.
          } else {
            showToast({
              message:
                error instanceof Error
                  ? error.message
                  : 'Could not restore this prototype version. Try again in a moment.',
              severity: 'error',
              position: 'top-center',
            });
          }
        }
      }

      // If we have a demo link ID, check if it's different from current
      if (prototyperDemoLinkId) {
        const currentId = currentPrototypeId;

        // Normalize both IDs to numbers for comparison
        const normalizedCurrentId = currentId
          ? typeof currentId === 'string'
            ? parseInt(currentId, 10)
            : currentId
          : null;
        const normalizedRequestedId =
          typeof prototyperDemoLinkId === 'string'
            ? parseInt(prototyperDemoLinkId, 10)
            : prototyperDemoLinkId;

        // Only fetch if it's a different prototyper or we don't have data yet
        if (normalizedCurrentId !== normalizedRequestedId) {
          try {
            await fetchPrototyperById(prototyperDemoLinkId);
          } catch (error) {
            console.error('Failed to fetch prototyper:', error);
            return;
          }
        }
      }
    },
    [
      fetchPrototyperById,
      currentPrototypeId,
      onPrototyperWorkbenchRestore,
      isStreamingPrototype,
      currentProjectId,
      currentConversationId,
      isWeb,
      currentMode,
      facade.mode,
      messages,
      turnVersions,
      getWorkbenchSnapshotIndexEntry,
      continueUnfinishedTurn,
      buildAgain,
    ]
  );

  return {
    handleGeneratePrototypeFromUncover,
    handleBackToUncover,
    handleRetryPrototyper,
    handlePrototyperClick,
    resetBuildStreamLatch,
    continuingTurnId,
    continuingTurnLabel,
  };
}

export type ConversationPrototypeStream = ReturnType<typeof useConversationPrototypeStream>;
